// YERİ: src/app/domains/site/sections/community.sections.ts
//
// TOPLULUK BÖLÜMLERİ — vaaz/sohbet arşivi, etkinlik programı, bağış kampanyası.
//
// Üçü de veriyi kiracının PORTAL kayıtlarından çeker. Yayımlanmamış vaaz ve
// iptal edilmiş etkinlik uca hiç gelmez (denetim sunucuda).
//
// Kampanya bölümünde YALNIZ TOPLAM tutar gösterilir; bağışçı adı ve tekil
// bağışlar hiçbir koşulda dışarı çıkmaz.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import {
  TenantWebService, TenantSermon, TenantPublicEvent, TenantCampaign,
} from '../core/tenant-web.service';

/**
 * Sunucudan gelen tarih ZAMAN DİLİMSİZ (naive) duvar saati: kiracı 19:00
 * girdiyse ziyaretçi de 19:00 görmeli. Zaman eki kırpılıp UTC olarak
 * ayrıştırılır ve `timeZone:'UTC'` ile basılır — hangi ülkeden bakılırsa
 * bakılsın girilen saatin aynısı çıkar.
 */
export function duvarTarihi(iso: string | null | undefined, lang: string, saatli: boolean): string {
  if (!iso) return '';
  const temiz = iso.replace(/(Z|[+-]\d{2}:?\d{2})$/i, '');
  const d = new Date(temiz + 'Z');
  if (Number.isNaN(d.getTime())) return '';

  const option: Intl.DateTimeFormatOptions = saatli
    ? { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
    : { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' };

  try {
    return new Intl.DateTimeFormat(lang, option).format(d);
  } catch {
    // Geçersiz dil kodu Intl'i patlatır; site bir tarih yüzünden çökmemeli.
    return new Intl.DateTimeFormat('en', option).format(d);
  }
}

/** Para birimi biçimlendirme — sabit 'tr-TR' YOK, site dilini kullanır. */
/**
 * Tutari para birimiyle basar.
 *
 * `varsayilan` — satirin KENDI birimi yoksa kullanilacak kod. Oda tipi, ders,
 * pansiyon birimi gibi tablolarda Currency kolonu YOK; oralarda sitenin birimi
 * (SiteChromeText.paraKodu, sunucudan sirketin Currency alani) gecilir.
 * Burada sabit bir yedek tutmak, Toronto'daki bir otelin gecelik ucretini
 * TL ile ilan etmesine yol aciyordu.
 */
export function paraBirimi(
  amount: number | null | undefined,
  kod: string | null | undefined,
  lang: string,
  varsayilan = 'CAD',
): string {
  if (amount == null) return '';
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency', currency: (kod || varsayilan).toUpperCase(), maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Geçersiz para birimi kodu Intl'i patlatır; sayıyı kodla birlikte bas.
    return `${Math.round(amount).toLocaleString(lang)} ${kod || varsayilan}`.trim();
  }
}

// ══════════════════════════════════════════════════════════════════════════
// VAAZ / SOHBET ARŞİVİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-sermon-list',
  standalone: true,
  template: `
    @if (vaaz().length) {
    <section id="sermons" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <ul class="mt-10 space-y-4">
          @for (v of vaaz(); track v.id) {
            <li class="rounded-2xl border border-neutral-200 bg-white p-5 transition hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                @if (v.deliveredAt) { <span>{{ date(v) }}</span> }
                @if (v.category) {
                  <span class="rounded-full bg-neutral-100 px-2 py-0.5 font-medium dark:bg-neutral-800">{{ v.category }}</span>
                }
              </div>
              <h3 class="mt-2 text-lg font-bold text-neutral-900 dark:text-white">{{ v.title }}</h3>
              @if (v.speaker) {
                <p class="mt-0.5 text-sm font-medium" style="color:var(--site-primary)">{{ v.speaker }}</p>
              }
              @if (v.topic) {
                <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('sr.topic') }}: {{ v.topic }}</p>
              }
              @if (v.summary) {
                <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ v.summary }}</p>
              }
              @if (v.mediaUrl) {
                <a [href]="v.mediaUrl" target="_blank" rel="noopener noreferrer"
                   class="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style="color:var(--site-primary)">{{ text.t('sr.listen') }}</a>
              }
            </li>
          }
        </ul>
      </div>
    </section>
    }
  `,
})
export class SermonListSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected vaaz = signal<TenantSermon[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const qty = Number(cfg(this.config(), 'count', 6)) || 6;
    this.svc.getSermons(id, qty).subscribe({
      next: (l) => this.vaaz.set(l),
      error: () => this.vaaz.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', 'Vaaz ve Sohbetler'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected date = (v: TenantSermon) => duvarTarihi(v.deliveredAt, this.text.lang(), false);
}

// ══════════════════════════════════════════════════════════════════════════
// ETKİNLİK PROGRAMI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-community-events',
  standalone: true,
  template: `
    @if (etkinlik().length) {
    <section id="events" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <ul class="mt-10 space-y-4">
          @for (e of etkinlik(); track e.id) {
            <li class="flex gap-5 rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
              <!-- Gün/ay bloğu: takvim yaprağı gibi okunsun diye ayrı sütun -->
              @if (e.startTime) {
                <div class="flex w-16 shrink-0 flex-col items-center justify-center rounded-xl py-2 text-white" style="background:var(--site-primary)">
                  <span class="text-2xl font-bold leading-none tabular-nums">{{ gun(e) }}</span>
                  <span class="mt-0.5 text-[11px] font-medium uppercase">{{ ay(e) }}</span>
                </div>
              }
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                  @if (e.startTime) { <span>{{ saat(e) }}</span> }
                  @if (e.category) {
                    <span class="rounded-full bg-neutral-100 px-2 py-0.5 font-medium dark:bg-neutral-800">{{ e.category }}</span>
                  }
                </div>
                <h3 class="mt-1 text-lg font-bold text-neutral-900 dark:text-white">{{ e.title }}</h3>
                @if (e.speaker) {
                  <p class="mt-0.5 text-sm font-medium" style="color:var(--site-accent)">{{ e.speaker }}</p>
                }
                @if (e.description) {
                  <p class="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ e.description }}</p>
                }
                <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                  @if (e.location) { <span>{{ text.t('ev.place') }}: {{ e.location }}</span> }
                  @if (e.capacity) { <span>{{ text.t('ev.capacity') }}: {{ e.capacity }}</span> }
                </div>
              </div>
            </li>
          }
        </ul>
      </div>
    </section>
    }
  `,
})
export class CommunityEventsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected etkinlik = signal<TenantPublicEvent[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const scope = String(cfg(this.config(), 'scope', 'upcoming')) as 'upcoming' | 'past' | 'all';
    const qty = Number(cfg(this.config(), 'count', 5)) || 5;
    this.svc.getReligiousEvents(id, scope, qty).subscribe({
      next: (l) => this.etkinlik.set(l),
      error: () => this.etkinlik.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.etkinlikProgrami')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  /** Gün/ay doğrudan dizeden okunuyor — saat dilimi kaydırması olmasın diye. */
  protected gun = (e: TenantPublicEvent) => (e.startTime || '').slice(8, 10);

  protected ay = (e: TenantPublicEvent): string => {
    const t = duvarTarihi(e.startTime, this.text.lang(), false);
    // "01 Mart 2026" → "Mart". Biçim dile göre değişebildiği için ikinci
    // parçayı almak yerine sayı olmayan en uzun parçayı seçiyoruz.
    const parcalar = t.split(/[\s,./-]+/).filter(p => p && !/^\d+$/.test(p));
    return parcalar[0] ?? '';
  };

  protected saat = (e: TenantPublicEvent) => duvarTarihi(e.startTime, this.text.lang(), true);
}

// ══════════════════════════════════════════════════════════════════════════
// BAĞIŞ KAMPANYALARI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-donation-campaigns',
  standalone: true,
  template: `
    @if (kampanya().length) {
    <section id="campaigns" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (k of kampanya(); track k.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              <div class="flex items-start justify-between gap-2">
                <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ k.name }}</h3>
                @if (completed(k)) {
                  <span class="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ text.t('cp.completed') }}</span>
                }
              </div>
              @if (k.category) {
                <p class="mt-1 text-xs font-medium uppercase tracking-wide" style="color:var(--site-accent)">{{ k.category }}</p>
              }
              @if (k.description) {
                <p class="mt-2 line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ k.description }}</p>
              }

              <!-- İlerleme yalnız hedef girilmişse anlamlı: hedefsiz bir
                   çubuk neye göre dolduğunu söylemez. -->
              @if (ilerlemeVar()) {
                <div class="mt-5">
                  <div class="flex items-baseline justify-between text-sm">
                    <span class="font-bold" style="color:var(--site-primary)">{{ collected(k) }}</span>
                    @if (k.goalAmount) {
                      <span class="text-xs text-neutral-500 dark:text-neutral-400">/ {{ hedef(k) }}</span>
                    }
                  </div>
                  @if (k.goalAmount) {
                    <div class="mt-2 h-2.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                      <div class="h-full rounded-full transition-all" [style.width.%]="oran(k)" style="background:var(--site-primary)"></div>
                    </div>
                    <p class="mt-1.5 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">%{{ oran(k) }}</p>
                  }
                </div>
              }

              @if (ctaText()) {
                <a [href]="ctaUrl()" class="mt-6 inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                   style="background:var(--site-primary)">{{ ctaText() }}</a>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class DonationCampaignsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected kampanya = signal<TenantCampaign[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const qty = Number(cfg(this.config(), 'count', 3)) || 3;
    const sadeceAktif = String(cfg(this.config(), 'activeOnly', 'true')) !== 'false';
    this.svc.getCampaigns(id, sadeceAktif, qty).subscribe({
      next: (l) => this.kampanya.set(l),
      error: () => this.kampanya.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.bagisKampanyalari')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', ''));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));

  /** Toplanan tutarı gizlemek isteyen dernek için kapatılabilir. */
  protected ilerlemeVar = computed(() => String(cfg(this.config(), 'showProgress', 'true')) !== 'false');

  protected completed = (k: TenantCampaign) => (k.status || '').toLowerCase() === 'completed';

  protected collected = (k: TenantCampaign) => paraBirimi(k.raisedAmount, k.currency, this.text.lang(), this.text.currencyKey());
  protected hedef = (k: TenantCampaign) => paraBirimi(k.goalAmount, k.currency, this.text.lang(), this.text.currencyKey());

  /** Hedefi aşan kampanyada çubuk %100'de durur; taşarsa görsel bozulurdu. */
  protected oran = (k: TenantCampaign): number => {
    const h = k.goalAmount ?? 0;
    if (h <= 0) return 0;
    return Math.min(100, Math.round((k.raisedAmount / h) * 100));
  };
}
